import { Industry } from './listconfigurator/listconfigurator.component';

export interface CompanyPropertyConfig {
    backendKey: string; // Key used by the backend API
    label: string;      // UI display name / Column name (defined once here)
    category: 'basic' | 'contact' | 'additional';
    defaultSelected?: boolean; // Optional: true if selected by default
}

// SINGLE SOURCE OF TRUTH for company information properties
export const COMPANY_PROPERTIES_CONFIG: CompanyPropertyConfig[] = [
    { backendKey: 'address',         label: 'Anschrift',           category: 'basic', defaultSelected: true },
    { backendKey: 'website',         label: 'Website',             category: 'basic', },
    { backendKey: 'google_rating',   label: 'Google Rating',       category: 'basic',  },
    { backendKey: 'emails_csv',      label: 'Email',               category: 'basic', },
    { backendKey: 'instagram_url',   label: 'Instagram Account',   category: 'contact' },
    { backendKey: 'facebook_url',    label: 'Facebook Account',    category: 'contact' },
    { backendKey: 'linked_in_url',   label: 'LinkedIn Profil',     category: 'contact' },
    { backendKey: 'impressum_url',   label: 'Impressum',           category: 'additional' },
    { backendKey: 'job_url',         label: 'Job Link',            category: 'additional' },
    { backendKey: 'contact_url',     label: 'Kontakt',             category: 'additional' },
];

export interface StateRow2 {
    label: string;
    defaultSelected: boolean;
}

export const STATES_PROPERTIES_CONFIG: StateRow2[] = [
    { label: 'Deutschlandweit',          defaultSelected: true },
    { label: 'Baden-Württemberg',         defaultSelected: true },
    { label: 'Bayern',                    defaultSelected: true },
    { label: 'Berlin',                    defaultSelected: true },
    { label: 'Brandenburg',               defaultSelected: true },
    { label: 'Bremen',                    defaultSelected: true },
    { label: 'Hamburg',                   defaultSelected: true },
    { label: 'Hessen',                    defaultSelected: true },
    { label: 'Mecklenburg-Vorpommern',    defaultSelected: true },
    { label: 'Niedersachsen',             defaultSelected: true },
    { label: 'Nordrhein-Westfalen',       defaultSelected: true },
    { label: 'Rheinland-Pfalz',           defaultSelected: true },
    { label: 'Saarland',                  defaultSelected: true },
    { label: 'Sachsen',                   defaultSelected: true },
    { label: 'Sachsen-Anhalt',            defaultSelected: true },
    { label: 'Schleswig-Holstein',        defaultSelected: true },
    { label: 'Thüringen',                 defaultSelected: true },

];

// Derived: List of labels that are selected by default
export const DEFAULT_SELECTED_LABELS: string[] = COMPANY_PROPERTIES_CONFIG
    .filter(prop => prop.defaultSelected)
    .map(prop => prop.label);

export const DEFAULT_SELECTED_REGIONS: string[] = STATES_PROPERTIES_CONFIG
    .filter(prop => prop.defaultSelected)
    .map(prop => prop.label);


// Derived: Map from backendKey to UI Label (replaces old PROP_TO_LABEL)
export const BACKEND_KEY_TO_UI_LABEL_MAP: Record<string, string> = Object.fromEntries(
    COMPANY_PROPERTIES_CONFIG.map(prop => [prop.backendKey, prop.label])
);

// Derived: Map from UI Label to backendKey (replaces old LABEL_TO_PROP)
export const UI_LABEL_TO_BACKEND_KEY_MAP: Record<string, string> = Object.fromEntries(
    COMPANY_PROPERTIES_CONFIG.map(prop => [prop.label, prop.backendKey])
);

// Your existing constants - ensure their keys (which are labels) match the 'label' values in COMPANY_PROPERTIES_CONFIG
export const COMPANY_INFO_COLUMN_MAP: Record<string, string[]> = {
    'Google Rating': ['Google Rating', 'Rating Anzahl'],
    'Anschrift': ['Stadt', 'Postleihzahl', 'Adresse'],
};

export const COLUMN_PATTERN_GENERATORS: Record<string, (i: number) => string | number> = {
    'Name': i => `Firma${i + 1}`,
    'Stadt': i => `Berlin`,
    'Postleihzahl': i => `10115`,
    'Adresse': i => `Beispielstraße ${i + 1}`,
    'Website': i => `www.firma${i + 1}.de`,
    'Google Rating': () => (Math.random() * (5 - 4) + 4).toFixed(1),
    'Rating Anzahl': () => Math.floor(Math.random() * (1000 - 10 + 1)) + 10,
    'Email': i => `kontakt@firma${i + 1}.de`,
    'Telefon': i => `01234-56${1000 + i}`,
    'Instagram Account': i => `instagram.com/@firma${i + 1}`,
    'Facebook Account': i => `fb.com/firma${i + 1}`,
    'LinkedIn Profil': i => `linkedin.com/in/firma${i + 1}`,
    'Impressum': i => `firma${i + 1}.de/impressum`,
    'Job Link': i => `firma${i + 1}.de/jobs`,
    'Kontakt': i => `firma${i + 1}.de/kontakt`,
};

/** Shape of the data we return to the modal */
export interface ColumnExplanation {
    title: string;
    shortDescription: string;
    longDescription: string;
}

/** Plain map -- extend or localise as you like */
export const COLUMN_TEXT: Record<string, ColumnExplanation> = {
    Anschrift: {
        title: 'Anschrift',
        shortDescription: 'Postadresse des Unternehmens.',
        longDescription:
            'Enthält Straße, Hausnummer, Postleitzahl und Ort der Firma. ' +
            'Wird für postalische Korrespondenz, Standort-Analysen und Geocoding verwendet.',
    },

    Website: {
        title: 'Website',
        shortDescription: 'URL der offiziellen Firmenwebseite.',
        longDescription:
            'Verweist auf die Haupt-Domain des Unternehmens (https://…). ' +
            'Sie dient als zentrale Informationsquelle für Produkte, Dienstleistungen und Kontaktmöglichkeiten.',
    },

    'Google Rating': {
        title: 'Google Rating',
        shortDescription: 'Durchschnittliche Sternebewertung auf Google.',
        longDescription:
            'Aggregierte Nutzerbewertungen (1–5 Sterne), wie sie in Google Maps bzw. im Google-Business-Profil erscheinen. ' +
            'Gibt einen schnellen Eindruck von Kundenzufriedenheit und Reputation; die Werte können sich laufend ändern.',
    },

    Email: {
        title: 'E-Mail',
        shortDescription: 'Öffentliche Kontakt-E-Mail-Adresse.',
        longDescription:
            'Eine oder mehrere (kommagetrennte) E-Mail-Adressen, die das Unternehmen für allgemeine Anfragen oder Support bereitstellt. ' +
            'Enthält nur verifizierte und öffentlich zugängliche Adressen.',
    },

    'Instagram Account': {
        title: 'Instagram-Account',
        shortDescription: 'Link zum offiziellen Instagram-Profil.',
        longDescription:
            'URL zum Instagram-Konto des Unternehmens (@handle). ' +
            'Hilft, Social-Media-Reichweite, visuelles Branding und Engagement zu analysieren.',
    },

    'Facebook Account': {
        title: 'Facebook-Account',
        shortDescription: 'Link zur Facebook-Seite des Unternehmens.',
        longDescription:
            'URL zur offiziellen Facebook-Page. ' +
            'Enthält Beiträge, Bewertungen und Community-Interaktionen; nützlich für Marketing- und Reichweiten-Analysen.',
    },

    'LinkedIn Profil': {
        title: 'LinkedIn-Profil',
        shortDescription: 'Link zur LinkedIn-Unternehmensseite.',
        longDescription:
            'URL zur Company-Page auf LinkedIn. ' +
            'Bietet Einblicke in Mitarbeiterzahlen, Branchenrelevanz und aktuelle Stellenanzeigen – wichtig für B2B- und Recruiting-Analysen.',
    },

    Impressum: {
        title: 'Impressum',
        shortDescription: 'Rechtliche Anbieterinformationen.',
        longDescription:
            'Link zur Impressums- bzw. Legal-Seite gemäß § 5 TMG. ' +
            'Dort stehen Firmenanschrift, Vertretungsberechtigte, Registerangaben und Kontaktmöglichkeiten; unerlässlich für rechtliche Transparenz.',
    },

    'Job Link': {
        title: 'Job-Link',
        shortDescription: 'URL zur Karriereseite oder Job-Übersicht.',
        longDescription:
            'Verweist direkt auf die Karriererubrik der Website oder ein Bewerber-Tracking-System. ' +
            'Erlaubt Rückschlüsse auf Personalbedarf, Wachstumspläne und Unternehmenskultur.',
    },

    Kontakt: {
        title: 'Kontakt',
        shortDescription: 'Link zur Kontakt- bzw. Support-Seite.',
        longDescription:
            'URL zu einem Kontaktformular, Support-Portal oder Live-Chat. ' +
            'Bietet Interessenten und Kunden einen schnellen Draht zum Unternehmen und enthält häufig zusätzliche Telefonnummern oder Servicezeiten.',
    },
};
/**
 * Look up column information by label.
 * Falls nichts hinterlegt ist, liefern wir generische Platzhalter-Texte.
 */
export function getColumnExplanation(label: string): ColumnExplanation {
    return (
        COLUMN_TEXT[label] ?? {
            title: label,
            shortDescription: 'Keine Kurzbeschreibung verfügbar.',
            longDescription:
                'Für diese Spalte liegt noch keine ausführliche Beschreibung vor. Bitte bei Bedarf ergänzen.',
        }
    );
}

export interface IndustryOverviewEntry {
    slug: string;
    name: string;
    thumbnail?: string | null;
    rowCount?: number | null;
}

export const AVAILABLE_INDUSTRIES_DATASTRUCT: IndustryOverviewEntry[] = [
    { slug: 'autohaendler', name: 'Autohändler', rowCount: 12495},
    { slug: 'autowerkstatt', name: 'Autowerkstätten', rowCount: 26906 },
    { slug: 'fotograf', name: 'Fotografen', rowCount: 26607 },
    { slug: 'friseursalon', name: 'Friseursalons', rowCount: 25158 },
    { slug: 'verein', name: 'Vereine', rowCount: 24639 },
    { slug: 'zahnarzt', name: 'Zahnärzte', rowCount: 24169 },
    { slug: 'restaurant', name: 'Restaurants', rowCount: 23737 },
    { slug: 'physiotherapeut', name: 'Physiotherapeuten', rowCount: 23181 },
    { slug: 'vereinigung__organisation', name: 'Vereinigungen / Organisationen', rowCount: 22654 },
    { slug: 'bauunternehmen', name: 'Bauunternehmen', rowCount: 21894 },
    { slug: 'werbeagentur', name: 'Werbeagenturen', rowCount: 20021 },
    { slug: 'maler', name: 'Maler', rowCount: 17490 },
    { slug: 'schoenheitssalon', name: 'Schönheitssalons', rowCount: 16689 },
    { slug: 'onlinemarketingunternehmen', name: 'Online-Marketing-Unternehmen', rowCount: 16634 },
    { slug: 'immobilienagentur', name: 'Immobilienagenturen', rowCount: 16558 },
    { slug: 'architekt', name: 'Architekten', rowCount: 16383, thumbnail: 'assets/images/industry_overview/Architekten.webp' },
    { slug: 'reinigungsdienst', name: 'Reinigungsdienste', rowCount: 15619 },
    { slug: 'immobilienmakler', name: 'Immobilienmakler', rowCount: 14338 },
    { slug: 'elektriker', name: 'Elektriker', rowCount: 14157 },
    { slug: 'gartenbauer', name: 'Gartenbauer', rowCount: 14086 },
    { slug: 'sanitaerinstallateur', name: 'Sanitärinstallateure', rowCount: 12681 },
    { slug: 'hersteller', name: 'Hersteller', rowCount: 12424 },
    { slug: 'bekleidungsgeschaeft', name: 'Bekleidungsgeschäfte', rowCount: 11885 },
    { slug: 'webdesigner', name: 'Webdesigner', rowCount: 11575 },
    { slug: 'baeckerei', name: 'Bäckereien', rowCount: 11550 },
    { slug: 'cafe', name: 'Cafés', rowCount: 11229 },
    { slug: 'dachdecker', name: 'Dachdecker', rowCount: 10328 },
    { slug: 'sportverein', name: 'Sportvereine', rowCount: 9964 },
    { slug: 'taxiunternehmen', name: 'Taxiunternehmen', rowCount: 9593 },
    { slug: 'fahrschule', name: 'Fahrschulen', rowCount: 9441 },
    { slug: 'hausmeisterservice', name: 'Hausmeisterservices', rowCount: 9419 },
    { slug: 'moebeltischler', name: 'Möbeltischler', rowCount: 9219 },
    { slug: 'nagelstudio', name: 'Nagelstudios', rowCount: 9164 },
    { slug: 'ingenieur', name: 'Ingenieure', rowCount: 8985 },
    { slug: 'gutachter', name: 'Gutachter', rowCount: 8742 },
    { slug: 'bar', name: 'Bars', rowCount: 8495 },
    { slug: 'hausverwaltungsunternehmen', name: 'Hausverwaltungsunternehmen', rowCount: 8423 },
    { slug: 'metallbauunternehmen', name: 'Metallbauunternehmen', rowCount: 8163 },
    { slug: 'lebensmittelhaendler', name: 'Lebensmittelhändler', rowCount: 7853 },
    { slug: 'logopaede', name: 'Logopäden', rowCount: 7594 },
    { slug: 'apotheke', name: 'Apotheken', rowCount: 7338 },
    { slug: 'grosshaendler', name: 'Großhändler', rowCount: 7294 },
    { slug: 'physiotherapiezentrum', name: 'Physiotherapiezentren', rowCount: 7203 },
    { slug: 'supermarkt', name: 'Supermärkte', rowCount: 7202 },
    { slug: 'friedhof', name: 'Friedhöfe', rowCount: 7151 },
    { slug: 'heizungs_und_klimatechnikbetrieb', name: 'Heizungs- und Klimatechnikbetriebe', rowCount: 7017 },
    { slug: 'eventmanagementfirma', name: 'Eventmanagementfirmen', rowCount: 6960 },
    { slug: 'zimmermann', name: 'Zimmerleute', rowCount: 6768 },
    { slug: 'moebelgeschaeft', name: 'Möbelgeschäfte', rowCount: 6686 },
    { slug: 'ergotherapeut', name: 'Ergotherapeuten', rowCount: 6627 },
    { slug: 'bestattungsinstitut', name: 'Bestattungsinstitute', rowCount: 6574 },
    { slug: 'pizzeria', name: 'Pizzerien', rowCount: 6359 },
    { slug: 'italienisches_restaurant', name: 'Italienische Restaurants', rowCount: 6277 },
    { slug: 'lkwspedition', name: 'LKW-Speditionen', rowCount: 6270 },
    { slug: 'auftragnehmer', name: 'Auftragnehmer', rowCount: 6239 },
    { slug: 'frachtspeditionsdienst', name: 'Frachtspeditionsdienste', rowCount: 6155 },
    { slug: 'fleischerei', name: 'Fleischereien', rowCount: 5900 },
    { slug: 'landschaftsgestalter', name: 'Landschaftsgestalter', rowCount: 5794 },
    { slug: 'transportunternehmen', name: 'Transportunternehmen', rowCount: 5776 },
    { slug: 'fahrradgeschaeft', name: 'Fahrradgeschäfte', rowCount: 5732 },
    { slug: 'solartechnikanbieter', name: 'Solartechnikanbieter', rowCount: 5519 },
    { slug: 'fensteranbieter', name: 'Fensteranbieter', rowCount: 5102 },
    { slug: 'maschinenbauunternehmen', name: 'Maschinenbauunternehmen', rowCount: 5048 },
    { slug: 'umzugs_und_lagerservice', name: 'Umzugs- und Lagerservices', rowCount: 4958 },
    { slug: 'metallverarbeitungsunternehmen', name: 'Metallverarbeitungsunternehmen', rowCount: 4896 },
    { slug: 'druckerei', name: 'Druckereien', rowCount: 4829 },
    { slug: 'hausreinigungsdienst', name: 'Hausreinigungsdienste', rowCount: 4560 },
    { slug: 'weinhandlung', name: 'Weinhandlungen', rowCount: 4556 },
    { slug: 'musikschule', name: 'Musikschulen', rowCount: 4552 },
    { slug: 'veranstaltungsstaette', name: 'Veranstaltungsstätten', rowCount: 4454 },
    { slug: 'hochzeitsfotograf', name: 'Hochzeitsfotografen', rowCount: 4429 },
    { slug: 'logistikdienst', name: 'Logistikdienste', rowCount: 4332 },
    { slug: 'mietwagenanbieter', name: 'Mietwagenanbieter', rowCount: 4240 },
    { slug: 'handwerk', name: 'Handwerksbetriebe', rowCount: 4116 },
    { slug: 'geschaeft', name: 'Geschäfte', rowCount: 4098 },
    { slug: 'kunstgalerie', name: 'Kunstgalerien', rowCount: 3852 },
    { slug: 'innenarchitekt', name: 'Innenarchitekten', rowCount: 3819 },
    { slug: 'heizungsmonteur', name: 'Heizungsmonteure', rowCount: 3812 },
    { slug: 'sanitaetshaus', name: 'Sanitätshäuser', rowCount: 3756 },
    { slug: 'kosmetikgeschaeft', name: 'Kosmetikgeschäfte', rowCount: 3739 },
    { slug: 'sanitaertechnik', name: 'Sanitärtechnik', rowCount: 3699 },
    { slug: 'hoergeraeteakustiker', name: 'Hörgeräteakustiker', rowCount: 3588 },
    { slug: 'oeffnungszeiten_hinzufuegen', name: 'Öffnungszeiten', rowCount: 3522 },
    { slug: 'technischer_berater', name: 'Technische Berater', rowCount: 3467 },
    { slug: 'schuhgeschaeft', name: 'Schuhgeschäfte', rowCount: 3456 },
    { slug: 'weinkellerei', name: 'Weinkellereien', rowCount: 3448 },
    { slug: 'sozialeinrichtung', name: 'Sozialeinrichtungen', rowCount: 3399 },
    { slug: 'geschaeft_fuer_heimtierbedarf', name: 'Geschäfte für Heimtierbedarf', rowCount: 3387 },
    { slug: 'softwareentwicklerhersteller', name: 'Softwareentwickler-Hersteller', rowCount: 3270 , thumbnail: 'assets/images/industry_overview/IT.webp' },
    { slug: 'fußballverein', name: 'Fußballvereine', rowCount: 3228 },
    { slug: 'kebabimbiss', name: 'Kebabimbisse', rowCount: 3227 },
    { slug: 'kneipe', name: 'Kneipen', rowCount: 3128 },
    { slug: 'gaertner', name: 'Gärtner', rowCount: 3103 },
    { slug: 'kfzersatzteilgeschaeft', name: 'Kfz-Ersatzteilgeschäfte', rowCount: 3062 },
    { slug: 'bushaltestelle', name: 'Bushaltestellen', rowCount: 3012 },
    { slug: 'bauprojektentwickler', name: 'Bauprojektentwickler', rowCount: 3004 },
    { slug: 'imbiss', name: 'Imbisse', rowCount: 2991 },
    { slug: 'transportdienst', name: 'Transportdienste', rowCount: 2988 },
    { slug: 'bauingenieur', name: 'Bauingenieure', rowCount: 2949 },
    { slug: 'reifengeschaeft', name: 'Reifengeschäfte', rowCount: 2903 },
    { slug: 'politische_partei', name: 'Politische Parteien', rowCount: 2864 },
    { slug: 'kfzpruefstelle', name: 'Kfz-Prüfstellen', rowCount: 2862 },
    { slug: 'tuerkisches_restaurant', name: 'Türkische Restaurants', rowCount: 2848 },
    { slug: 'campingplatz', name: 'Campingplätze', rowCount: 2847 },
    { slug: 'kieferorthopaede', name: 'Kieferorthopäden', rowCount: 2842 },
    { slug: 'fastfoodrestaurant', name: 'Fastfoodrestaurants', rowCount: 2819 },
    { slug: 'kosmetische_gesichtsbehandlungen', name: 'Kosmetische Gesichtsbehandlungen', rowCount: 2809 },
    { slug: 'spirituosengeschaeft', name: 'Spirituosengeschäfte', rowCount: 2809 },
    { slug: 'reinigungsunternehmen', name: 'Reinigungsunternehmen', rowCount: 2806 },
    { slug: 'eiscafe', name: 'Eiscafés', rowCount: 2799 },
    { slug: 'verpackungsunternehmen', name: 'Verpackungsunternehmen', rowCount: 2772 },
    { slug: 'grafikdesigner', name: 'Grafikdesigner', rowCount: 2697 },
    { slug: 'veranstaltungstechnikdienstleistung', name: 'Veranstaltungstechnik-Dienstleistungen', rowCount: 2680 },
    { slug: 'trockenbauunternehmen', name: 'Trockenbauunternehmen', rowCount: 2617 },
    { slug: 'griechisches_restaurant', name: 'Griechische Restaurants', rowCount: 2617 },
    { slug: 'fliesenleger', name: 'Fliesenleger', rowCount: 2616 },
    { slug: 'gemeinnuetzige_organisation', name: 'Gemeinnützige Organisationen', rowCount: 2604 },
    { slug: 'autoglaswerkstatt', name: 'Autoglaswerkstätten', rowCount: 2602 },
    { slug: 'website_hinzufuegen', name: 'Websites', rowCount: 2577 },
    { slug: 'marketingbuero', name: 'Marketingbüros', rowCount: 2558 },
    { slug: 'kosmetiker', name: 'Kosmetiker', rowCount: 2523 },
    { slug: 'deutsches_restaurant', name: 'Deutsche Restaurants', rowCount: 2470 },
    { slug: 'veranstaltungsraum', name: 'Veranstaltungsräume', rowCount: 2459 },
    { slug: 'spielwarengeschaeft', name: 'Spielwarengeschäfte', rowCount: 2418 },
    { slug: 'juwelier', name: 'Juweliere', rowCount: 2416 },
    { slug: 'elektrotechniker', name: 'Elektrotechniker', rowCount: 2396 },
    { slug: 'shishabar', name: 'Shisha-Bars', rowCount: 2356 },
    { slug: 'tennisverein', name: 'Tennisvereine', rowCount: 2338 },
    { slug: 'solarenergieunternehmen', name: 'Solarenergieunternehmen', rowCount: 2334 },
    { slug: 'fotostudio', name: 'Fotostudios', rowCount: 2334 },
    { slug: 'juweliergeschaeft', name: 'Juweliergeschäfte', rowCount: 2326 },
    { slug: 'generalunternehmer', name: 'Generalunternehmer', rowCount: 2319 },
    { slug: 'gastrokneipe', name: 'Gastrokneipen', rowCount: 2304 },
    { slug: 'fotoservice', name: 'Fotoservices', rowCount: 2280 },
    { slug: 'bodenleger', name: 'Bodenleger', rowCount: 2243 },
    { slug: 'damenmodengeschaeft', name: 'Damenmodengeschäfte', rowCount: 2208 },
    { slug: 'blumengeschaeft', name: 'Blumengeschäfte', rowCount: 2205 },
    { slug: 'gebrauchtwagenhaendler', name: 'Gebrauchtwagenhändler', rowCount: 2161 },
    { slug: 'schuetzenverein', name: 'Schützenvereine', rowCount: 2132 },
    { slug: 'goldschmied', name: 'Goldschmiede', rowCount: 2113 },
    { slug: 'sportanlage', name: 'Sportanlagen', rowCount: 2093 },
    { slug: 'baumarkt', name: 'Baumärkte', rowCount: 2062 },
    { slug: 'kuechenmoebelgeschaeft', name: 'Küchenmöbelgeschäfte', rowCount: 2061 },
    { slug: 'telefonnummer_hinzufuegen', name: 'Telefonnummern', rowCount: 2059 },
    { slug: 'kurierdienst', name: 'Kurierdienste', rowCount: 2044 },
    { slug: 'asiatisches_restaurant', name: 'Asiatische Restaurants', rowCount: 2037 },
    { slug: 'friseur', name: 'Friseure', rowCount: 2013 },
    { slug: 'einkaufszentrum', name: 'Einkaufszentren', rowCount: 1996 },
    { slug: 'glaser', name: 'Glaser', rowCount: 1935 },
    { slug: 'karosseriewerkstatt', name: 'Karosseriewerkstätten', rowCount: 1916 },
    { slug: 'pizzalieferdienst', name: 'Pizzalieferdienste', rowCount: 1898 },
    { slug: 'gemeinnuetzige_einrichtung', name: 'Gemeinnützige Einrichtungen', rowCount: 1889 },
    { slug: 'reparaturladen_fuer_mobiltelefone', name: 'Reparaturläden für Mobiltelefone', rowCount: 1873 },
    { slug: 'klimaanlagenanbieter', name: 'Klimaanlagenanbieter', rowCount: 1854 },
    { slug: 'bistro', name: 'Bistros', rowCount: 1852 },
    { slug: 'kuenstler', name: 'Künstler', rowCount: 1844 }
];

/**
 * Anzeige-Plural  ->  slug
 * (wird zur Laufzeit erzeugt, damit du nichts doppelt pflegen musst)
 */
export const NAME_TO_SLUG_MAP: Record<string, string> = Object.fromEntries(
    AVAILABLE_INDUSTRIES_DATASTRUCT.map(industry => [industry.name, industry.slug])
);

export const SLUG_TO_NAME_MAP: Record<string, string> = Object.fromEntries(
    AVAILABLE_INDUSTRIES_DATASTRUCT.map(industry => [industry.slug, industry.name])
);